import React from 'react';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import Icon from '@mdi/react';
import { mdiClipboardOutline } from '@mdi/js';
import {
  reduxForm,
  Field,
  formValueSelector,
  getFormSyncErrors,
} from 'redux-form';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import BlockUi from 'react-block-ui';
import TextField from '../../components/LogingTextField';
import TextAreaFieldWithSpeech from '../../components/QuillField/TextAreaFieldWithSpeech';
import ButtonCopy from '../../components/Button';
import TitleBox from './components/TitleBox';
import TextBox from './components/TextBox';
import { initialValuesSelector } from './reducer';
import { saveNote, SAVE_NOTE_REQUEST } from '../NotesPage/actions';
import { deleteNote, DELETE_NOTE_REQUEST, autoSaveNote } from './actions';
import {
  createLoadingSelector,
  emailSelector,
  speechSelector,
} from '../Main/reducer';
import { speechOn, speechOff } from '../Main/actions';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import config from '../../config';
import ReactGA from 'react-ga';
import { titleValidator } from '../../utils/validator';
import { Button } from 'reactstrap';

class NoteEditPage extends React.Component {
  state = {
    fileName: '',
    uploading: false,
    comprehension: false,
    superBill: false,
  };

  componentDidMount() {
    this.interval = setInterval(this.autoSave, config.autoSavePeriod);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  autoSave = () => {
    const { match, text, title, autoSaveNote, loading } = this.props;
    if (!loading && match.params.id) {
      autoSaveNote(match.params.id, text, title);
    }
  };

  handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    this.setState({ [name]: checked });
  };

  handleSave = (values) => {
    const { match, saveNote, formErrors, intl } = this.props;
    const { comprehension, superBill } = this.state;

    if (formErrors.title) {
      const errorMessage = intl.formatMessage({
        id:
          formErrors.title === 'required'
            ? 'Validation.error.required'
            : 'Validation.error.invalidTitle',
      });
      toast.error(errorMessage);
      return;
    }

    saveNote(
      match.params.id,
      values.title,
      values.text,
      comprehension,
      superBill
    );
  };

  render() {
    const {
      theme,
      history,
      match,
      text,
      title,
      email,
      loading,
      speech,
      speechOn,
      speechOff,
      role,
      handleSubmit,
    } = this.props;

    const { comprehension, superBill } = this.state;

    return (
      <BlockUi
        tag="div"
        blocking={loading}
        className="flex-fill d-flex flex-column"
      >
        <Helmet>
          <title>{title || 'Untitled Note'} | VoiceboxMD Web</title>
        </Helmet>
        <React.Fragment>
          <div className="d-flex" style={{ flex: '0 0' }}>
            <CopyToClipboard
              text={text}
              onCopy={() =>
                toast.info(`Note "${title || 'Untitled Note'}" copied!`)
              }
            >
              <ButtonCopy style={{ marginLeft: -24 }}>
                <Icon
                  path={mdiClipboardOutline}
                  size={1}
                  color={theme.color.default}
                  style={{ marginTop: -4, marginRight: 4 }}
                />
                <FormattedMessage id="NotePage.copyNote" />
              </ButtonCopy>
            </CopyToClipboard>
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '8px' }}>
              <Button color="secondary" onClick={history.goBack}>
                <FormattedMessage id="NoteEditPage.cancel" />
              </Button>
              <Button color="primary" onClick={handleSubmit(this.handleSave)}>
                <FormattedMessage id="NoteEditPage.save" />
              </Button>
            </div>
          </div>

          {role === 'pro' && !match.params.id && (
            <div className="d-flex mt-3">
              <label className="mr-4">
                <input
                  type="checkbox"
                  name="comprehension"
                  checked={comprehension}
                  onChange={this.handleCheckboxChange}
                  className="mr-1"
                />
                Request Comprehension
              </label>
              <label>
                <input
                  type="checkbox"
                  name="superBill"
                  checked={superBill}
                  onChange={this.handleCheckboxChange}
                  className="mr-1"
                />
                Request Super Bill
              </label>
            </div>
          )}

          <TitleBox>
            <FormattedMessage id="NoteEditPage.title">
              {(msg) => (
                <Field
                  name="title"
                  placeholder={msg}
                  component={TextField}
                  noBorder
                  speech={speech}
                  speechOn={speechOn}
                  speechOff={speechOff}
                />
              )}
            </FormattedMessage>
          </TitleBox>
          <TextBox>
            <FormattedMessage id="NoteEditPage.text">
              {(msg) => (
                <Field
                  name="text"
                  component={TextAreaFieldWithSpeech}
                  placeholder={msg}
                  textarea={true}
                  rows={20}
                  onMicClick={() =>
                    ReactGA.event({
                      category: 'dictation_web',
                      action: 'mic_click',
                      label: email.replace('@', '[at]'),
                    })
                  }
                  speech={speech}
                  speechOn={speechOn}
                  speechOff={speechOff}
                />
              )}
            </FormattedMessage>
          </TextBox>
        </React.Fragment>
      </BlockUi>
    );
  }
}

NoteEditPage = reduxForm({
  form: 'edit',
  validate: (values) => {
    const errors = {};
    if (!values.title) {
      errors.title = 'required';
    } else if (!titleValidator(values.title)) {
      errors.title = 'invalidTitle';
    }
    return errors;
  },
})(NoteEditPage);

NoteEditPage = withTheme(NoteEditPage);

const selector = formValueSelector('edit');

NoteEditPage = connect(
  (state) => ({
    initialValues: initialValuesSelector(state),
    text: selector(state, 'text'),
    title: selector(state, 'title'),
    email: emailSelector(state),
    speech: speechSelector(state),
    role: state.main.user.role,
    loading: createLoadingSelector([SAVE_NOTE_REQUEST, DELETE_NOTE_REQUEST])(
      state
    ),
    formErrors: getFormSyncErrors('edit')(state),
  }),
  { saveNote, deleteNote, autoSaveNote, speechOn, speechOff }
)(NoteEditPage);

NoteEditPage = injectIntl(NoteEditPage);

export default NoteEditPage;
